import type { AssignmentSource } from "modules/aiMapping/aiAssignmentSources";
import { useEffect, useMemo, useState } from "react";
import type { ProductOption } from "../measurementTypes";
import type {
  AiAssignmentPersistence,
  AiSavedAssignment,
  AssignedSource,
  ProductDraft,
} from "./aiAssignmentTypes";
import { createDraftId } from "./aiAssignmentTypes";

export default function useProductDrafts({
  sources,
  products,
  savedAiAssignments = [],
  persistence,
  onUpdateMapMeasurementProduct,
}: {
  sources: AssignmentSource[];
  products: ProductOption[];
  savedAiAssignments?: AiSavedAssignment[];
  persistence?: AiAssignmentPersistence;
  onUpdateMapMeasurementProduct: (featureId: string, product: ProductOption | null) => void;
}) {
  const [drafts, setDrafts] = useState<ProductDraft[]>([]);

  const sourceById = useMemo(
    () => new Map(sources.map((source) => [source.id, source])),
    [sources]
  );

  const compactProducts = useMemo(
    () =>
      products.slice().sort((a, b) => {
        const categoryA = a.organizationProductType?.name ?? "Uncategorized";
        const categoryB = b.organizationProductType?.name ?? "Uncategorized";
        return categoryA.localeCompare(categoryB) || (a.name ?? "").localeCompare(b.name ?? "");
      }),
    [products]
  );

  const updateDraft = (draftId: string, update: (draft: ProductDraft) => ProductDraft) =>
    setDrafts((current) => current.map((draft) => (draft.id === draftId ? update(draft) : draft)));

  const savedMapAssignmentKey = sources
    .filter((source) => source.kind === "map_measurement" && source.assignedProductId)
    .map((source) => `${source.id}:${source.assignedProductId}`)
    .sort()
    .join("|");

  useEffect(() => {
    if (!savedMapAssignmentKey) return;
    const savedSources = sources.filter(
      (source) => source.kind === "map_measurement" && source.assignedProductId
    );
    setDrafts((current) => {
      let next = current;
      savedSources.forEach((source) => {
        if (
          next.some((draft) =>
            draft.sources.some((assignment) => assignment.sourceId === source.id)
          )
        )
          return;
        const productIndex = next.findIndex(
          (draft) => draft.productId === source.assignedProductId
        );
        if (productIndex >= 0) {
          next = next.map((draft, index) =>
            index === productIndex
              ? {
                  ...draft,
                  sources: [...draft.sources, { id: createDraftId(), sourceId: source.id }],
                }
              : draft
          );
        } else {
          next = [
            ...next,
            {
              id: createDraftId(),
              productId: source.assignedProductId ?? null,
              sources: [{ id: createDraftId(), sourceId: source.id }],
              costs: [],
              open: false,
            },
          ];
        }
      });
      return next;
    });
  }, [savedMapAssignmentKey, sources]);

  const sourceByIdWithSaved = useMemo(
    () =>
      new Map<string, AssignmentSource>([
        ...sources.map((source) => [source.id, source] as const),
        ...savedAiAssignments.flatMap((assignment) => {
          const source = sources.find((candidate) => candidate.id === assignment.sourceId);
          return source ? [[source.id, source] as const] : [];
        }),
      ]),
    [sources, savedAiAssignments]
  );

  const savedAiKey = savedAiAssignments
    .filter((assignment) => assignment.productId || (assignment.additionalCostIds ?? []).length)
    .map(
      (assignment) =>
        `${assignment.sourceId}:${assignment.productId ?? ""}:${
          assignment.jobSize ? "job" : "cost"
        }:${(assignment.additionalCostIds ?? []).join(",")}`
    )
    .sort()
    .join("|");
  const sourcesKey = sources
    .map((source) => source.id)
    .sort()
    .join("|");

  useEffect(() => {
    if (!savedAiKey || !sourceByIdWithSaved.size) return;
    const entries = savedAiAssignments.filter(
      (assignment) => assignment.productId || (assignment.additionalCostIds ?? []).length
    );
    setDrafts((current) => {
      let next = current;
      entries.forEach((entry) => {
        const source = sourceByIdWithSaved.get(entry.sourceId);
        if (!source) return;
        const alreadyPresent = next.some(
          (draft) =>
            draft.productId === entry.productId &&
            (draft.sources.some((item) => item.sourceId === entry.sourceId) ||
              draft.costs.some((cost) =>
                cost.sources.some((item) => item.sourceId === entry.sourceId)
              ))
        );
        if (alreadyPresent) return;
        const productId = entry.productId ?? null;
        let productIndex = next.findIndex((draft) => draft.productId === productId);
        const assignment: AssignedSource = {
          id: createDraftId(),
          sourceId: entry.sourceId,
          stallMode: entry.stallMode,
        };
        if (productIndex >= 0) {
          next = next.map((draft, index) =>
            index === productIndex
              ? {
                  ...draft,
                  sources: entry.jobSize === false ? draft.sources : [...draft.sources, assignment],
                  costs: [
                    ...draft.costs,
                    ...(entry.additionalCostIds ?? [])
                      .filter((costId) => !draft.costs.some((cost) => cost.costId === costId))
                      .map((costId) => ({
                        id: createDraftId(),
                        costId,
                        sources: entry.jobSize === false ? [assignment] : ([] as AssignedSource[]),
                      })),
                  ],
                }
              : draft
          );
        } else {
          next = [
            ...next,
            {
              id: createDraftId(),
              productId,
              sources: entry.jobSize === false ? [] : [assignment],
              costs: (entry.additionalCostIds ?? []).map((costId) => ({
                id: createDraftId(),
                costId,
                sources: entry.jobSize === false ? [assignment] : ([] as AssignedSource[]),
              })),
              open: false,
            },
          ];
          productIndex = next.length - 1;
        }
      });
      return next;
    });
  }, [savedAiKey, sourcesKey]);

  const updateMapSourceProduct = (sourceId: string, productId: string | null) => {
    const source = sourceById.get(sourceId);
    if (source?.kind !== "map_measurement" || !source.mapMeasurementId) return;
    onUpdateMapMeasurementProduct(
      source.mapMeasurementId,
      compactProducts.find((product) => product.id === productId) ?? null
    );
  };

  const isLinearFeetAllowed = (productId: string | null) =>
    compactProducts.find((product) => product.id === productId)?.measurementJob === "LINEAR_FOOT";

  const addDraft = () =>
    setDrafts((current) => [
      ...current,
      { id: createDraftId(), productId: null, sources: [], costs: [], open: true },
    ]);

  const toggleOpen = (draftId: string) =>
    updateDraft(draftId, (item) => ({ ...item, open: !item.open }));

  const addSource = (draftId: string, costId: string | undefined, sourceId: string) => {
    const draft = drafts.find((item) => item.id === draftId);
    if (!sourceId || !draft || !draft.productId) return;
    const source = sourceById.get(sourceId);
    if (!costId) updateMapSourceProduct(sourceId, draft.productId);
    if (source && persistence && !costId && source.kind !== "map_measurement" && draft.productId) {
      persistence.onAssignSource(source, draft.productId, "count");
    }
    if (source && persistence && costId) {
      const additionalCostId = draft.costs.find((item) => item.id === costId)?.costId ?? "";
      persistence.onAssignCost(source, additionalCostId, draft.productId);
    }
    updateDraft(draftId, (current) => {
      const assignment = { id: createDraftId(), sourceId };
      if (!costId) return { ...current, sources: [...current.sources, assignment] };
      return {
        ...current,
        costs: current.costs.map((cost) =>
          cost.id === costId ? { ...cost, sources: [...cost.sources, assignment] } : cost
        ),
      };
    });
  };

  const removeJobSource = (draftId: string, assignment: AssignedSource) => {
    const draft = drafts.find((item) => item.id === draftId);
    const source = sourceById.get(assignment.sourceId);
    if (source && persistence && source.kind !== "map_measurement") {
      persistence.onUnassignSource(source, draft?.productId);
    }
    updateMapSourceProduct(assignment.sourceId, null);
    updateDraft(draftId, (d) => ({
      ...d,
      sources: d.sources.filter((s) => s.id !== assignment.id),
    }));
  };

  const removeCostSource = (draftId: string, costId: string, assignment: AssignedSource) => {
    const draft = drafts.find((item) => item.id === draftId);
    const source = sourceById.get(assignment.sourceId);
    const costIdValue = draft?.costs.find((cost) => cost.id === costId)?.costId;
    if (persistence && source && source.kind !== "map_measurement" && costIdValue) {
      persistence.onRemoveCost(source, costIdValue, draft?.productId);
    }
    updateDraft(draftId, (d) => ({
      ...d,
      costs: d.costs.map((cost) =>
        cost.id === costId
          ? { ...cost, sources: cost.sources.filter((s) => s.id !== assignment.id) }
          : cost
      ),
    }));
  };

  const changeProduct = (draftId: string, productId: string | null) => {
    const draft = drafts.find((item) => item.id === draftId);
    const nextProduct = compactProducts.find((product) => product.id === productId);
    const linearFeetOk = nextProduct?.measurementJob === "LINEAR_FOOT";
    draft?.sources.forEach((assignment) => {
      updateMapSourceProduct(assignment.sourceId, productId);
      const source = sourceById.get(assignment.sourceId);
      const mode =
        assignment.stallMode === "linear_ft" && !linearFeetOk
          ? "count"
          : assignment.stallMode ?? "count";
      if (source && persistence && source.kind !== "map_measurement") {
        if (draft.productId && draft.productId !== productId) {
          persistence.onUnassignSource(source, draft.productId);
        }
        if (productId) persistence.onAssignSource(source, productId, mode);
      }
    });
    draft?.costs.forEach((cost) => {
      if (!cost.costId) return;
      cost.sources.forEach((assignment) => {
        const source = sourceById.get(assignment.sourceId);
        if (source && persistence && source.kind !== "map_measurement") {
          if (draft.productId && draft.productId !== productId) {
            persistence.onRemoveCost(source, cost.costId as string, draft.productId);
          }
          if (productId) persistence.onAssignCost(source, cost.costId as string, productId);
        }
      });
    });
    updateDraft(draftId, (item) => ({
      ...item,
      productId,
      sources: item.sources.map((assignment) =>
        assignment.stallMode === "linear_ft" && !linearFeetOk
          ? { ...assignment, stallMode: "count" as const }
          : assignment
      ),
    }));
  };

  const changeAdditionalCost = (
    draftId: string,
    costRowId: string,
    additionalCostId: string | null
  ) => {
    const draft = drafts.find((item) => item.id === draftId);
    const cost = draft?.costs.find((item) => item.id === costRowId);
    if (!draft || !cost) return;
    cost.sources.forEach((assignment) => {
      const source = sourceById.get(assignment.sourceId);
      if (!source || !persistence || source.kind === "map_measurement") return;
      if (cost.costId) persistence.onRemoveCost(source, cost.costId, draft.productId);
      if (additionalCostId) persistence.onAssignCost(source, additionalCostId, draft.productId);
    });
    updateDraft(draftId, (item) => ({
      ...item,
      costs: item.costs.map((current) =>
        current.id === costRowId ? { ...current, costId: additionalCostId } : current
      ),
    }));
  };

  const removeAdditionalCost = (draftId: string, costRowId: string) => {
    const draft = drafts.find((item) => item.id === draftId);
    const cost = draft?.costs.find((item) => item.id === costRowId);
    if (!draft || !cost) return;
    if (cost.costId) {
      cost.sources.forEach((assignment) => {
        const source = sourceById.get(assignment.sourceId);
        if (source && persistence && source.kind !== "map_measurement") {
          persistence.onRemoveCost(source, cost.costId as string, draft.productId);
        }
      });
    }
    updateDraft(draftId, (item) => ({
      ...item,
      costs: item.costs.filter((current) => current.id !== costRowId),
    }));
  };

  const removeProduct = (draftId: string) => {
    const draft = drafts.find((item) => item.id === draftId);
    draft?.sources.forEach((assignment) => {
      const source = sourceById.get(assignment.sourceId);
      if (source && persistence && source.kind !== "map_measurement") {
        persistence.onUnassignSource(source, draft.productId);
      }
      updateMapSourceProduct(assignment.sourceId, null);
    });
    draft?.costs.forEach((cost) => {
      if (!cost.costId) return;
      cost.sources.forEach((assignment) => {
        const source = sourceById.get(assignment.sourceId);
        if (source && persistence && source.kind !== "map_measurement") {
          persistence.onRemoveCost(source, cost.costId as string, draft.productId);
        }
      });
    });
    setDrafts((current) => current.filter((item) => item.id !== draftId));
  };

  const changeStallMode = (
    draftId: string,
    assignmentId: string,
    mode: "count" | "linear_ft",
    costId?: string
  ) => {
    const draft = drafts.find((item) => item.id === draftId);
    const effectiveMode =
      mode === "linear_ft" && !isLinearFeetAllowed(draft?.productId ?? null) ? "count" : mode;
    const assignment = costId
      ? draft?.costs.find((c) => c.id === costId)?.sources.find((s) => s.id === assignmentId)
      : draft?.sources.find((s) => s.id === assignmentId);
    const source = assignment ? sourceById.get(assignment.sourceId) : undefined;
    if (source && persistence && source.kind !== "map_measurement" && draft?.productId) {
      persistence.onAssignSource(source, draft.productId, effectiveMode);
    }
    updateDraft(draftId, (d) => {
      const update = (item: AssignedSource) =>
        item.id === assignmentId ? { ...item, stallMode: effectiveMode } : item;
      return costId
        ? {
            ...d,
            costs: d.costs.map((cost) =>
              cost.id === costId ? { ...cost, sources: cost.sources.map(update) } : cost
            ),
          }
        : { ...d, sources: d.sources.map(update) };
    });
  };

  const addCostRow = (draftId: string) =>
    updateDraft(draftId, (item) => ({
      ...item,
      costs: [...item.costs, { id: createDraftId(), costId: null, sources: [] }],
    }));

  return {
    drafts,
    compactProducts,
    sourceById,
    availableSources: sources,
    isLinearFeetAllowed,
    addDraft,
    toggleOpen,
    addSource,
    removeJobSource,
    removeCostSource,
    changeProduct,
    changeAdditionalCost,
    removeAdditionalCost,
    removeProduct,
    changeStallMode,
    addCostRow,
  };
}
